//== per-step validation rules for the FHS redemption calculator
//== pure functions; each returns { valid: boolean, errors: { [field]: string } }
//== error strings are placeholder English; route through calculatorData.copy when CMS messages are added

import {
  calculate,
  PRODUCTS,
  originalEquitySharePct,
  isCurrentEquityLocked,
  resolveCurrentEquityPct,
  resolveInputs
} from './calculate.js'
import { formatMoney } from '../calc-shared/format.js'

const isPositiveFinite = (n) => typeof n === 'number' && Number.isFinite(n) && n > 0
const isNonNegativeFinite = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0

const result = (errors) => ({ valid: Object.keys(errors).length === 0, errors })

//== --- step 0: product selection ---
export function validateStep0(state, _scheme, { now = new Date() } = {}) {
  const errors = {}

  if (!state.product) {
    errors.product = 'Please select a product type'
  }

  if (!state.drawdownDate) {
    errors.drawdownDate = 'Please enter your drawdown date'
  } else {
    const drawdown = state.drawdownDate instanceof Date ? state.drawdownDate : new Date(state.drawdownDate)
    if (Number.isNaN(drawdown.getTime())) {
      errors.drawdownDate = 'Please enter a valid date'
    } else if (drawdown > now) {
      errors.drawdownDate = 'Drawdown date cannot be in the future'
    }
  }

  if (state.product === PRODUCTS.SELF_BUILD && state.buildComplete == null) {
    errors.buildComplete = 'Please indicate whether your build is complete'
  }

  return result(errors)
}

//== --- step 1: property details ---
export function validateStep1(state, scheme) {
  const errors = {}
  const isSelfBuild = state.product === PRODUCTS.SELF_BUILD
  const inputs = state.inputs ?? {}

  if (isSelfBuild) {
    if (!isPositiveFinite(inputs.buildCost)) {
      errors.buildCost = 'Please enter your build cost'
    }
    if (!isNonNegativeFinite(inputs.siteValue)) {
      errors.siteValue = 'Please enter your site value'
    }
  } else {
    if (!isPositiveFinite(inputs.purchasePrice)) {
      errors.purchasePrice = 'Please enter the purchase price'
    }
  }

  if (!isPositiveFinite(inputs.originalEquity)) {
    errors.originalEquity = 'Please enter your original equity amount'
  }

  //== when the current share is locked (SB, build incomplete) there is nothing
  //== for the customer to enter - the field is read-only and the value is
  //== derived from the original equity, which carries its own required errors.
  //== so skip the required check, and skip the over-original block below since
  //== the two are equal by construction (client 2026-07-21).
  const locked = isCurrentEquityLocked(state)
  const pct = locked ? resolveCurrentEquityPct(state) : inputs.currentEquityPct
  if (!locked && (!Number.isFinite(pct) || pct <= 0 || pct > 100)) {
    errors.currentEquityPct = 'Please enter a percentage between 0 and 100'
  }

  //== hard blocks (client sign-off 2026-07): the two step-1 warnings now bar
  //== Continue rather than just warning. the live data-gate callouts in the
  //== markup carry the user-facing message, so these keys deliberately have no
  //== data-error element - they only gate navigation via getValidSteps. the
  //== conditions mirror render.js computeOriginalEquityOverMax /
  //== computeCurrentEquityOverOriginal so the block and the callout agree.
  const share = originalEquitySharePct(state)
  const cap = scheme?.maxEquityPct
  if (Number.isFinite(cap) && Number.isFinite(share) && share > cap) {
    errors.originalEquityOverMax = 'Original equity share exceeds the scheme maximum'
  }
  if (!locked && Number.isFinite(share) && Number.isFinite(pct) && pct / 100 > share) {
    errors.currentEquityOverOriginal = 'Current equity share exceeds the original'
  }

  return result(errors)
}

//== --- step 2: current valuation ---
export function validateStep2(state) {
  const errors = {}
  if (!isPositiveFinite(state.inputs?.currentValuation)) {
    errors.currentValuation = 'Please enter your current valuation'
  }
  return result(errors)
}

//== --- step 3: redemption ---
export function validateStep3(state, scheme) {
  const errors = {}
  const { type, amount } = state.redemption ?? {}

  if (!type) {
    errors.type = 'Please choose a redemption type'
    return result(errors)
  }

  if (type === 'partial') {
    //== self-build: partial redemption is unavailable until the build is complete
    if (state.product === PRODUCTS.SELF_BUILD && state.buildComplete !== true) {
      errors.type = 'Partial redemption is not available until the build of your home is complete'
      return result(errors)
    }

    const calc = calculate({
      product: state.product,
      inputs: resolveInputs(state),
      redemption: state.redemption,
      scheme,
      early: state.isEarlyRedemption
    })

    if (!calc) {
      errors.amount = 'Please complete prior steps'
    } else if (!Number.isFinite(amount)) {
      errors.amount = 'Please enter a redemption amount'
    } else if (amount < calc.minPartial) {
      errors.amount = 'Below the minimum partial redemption'
    } else if (amount > calc.maxPartial) {
      //== prompt the user to switch to full when they exceed the cap, per the
      //== design summary's documented enhancement over the spreadsheet
      errors.amount = `Amount exceeds FHS's current equity value (${formatMoney(calc.maxPartial)}). Switch to a full redemption instead.`
    }
  } else if (type === 'full') {
    const sc = state.inputs?.serviceCharges
    if (sc != null && !isNonNegativeFinite(sc)) {
      errors.serviceCharges = 'Service charges cannot be negative'
    }
  } else {
    errors.type = 'Please choose a redemption type'
  }

  return result(errors)
}

//== --- aggregate: which step indices the user can navigate to ---
//== respects isEarlyRedemption: in early mode only the valuation step (2) is
//== skipped - the redemption step (3) is kept so the user still chooses full /
//== partial, then step 4. so a valid step 1 promotes step 3 directly.
export function getValidSteps(state, scheme, options = {}) {
  const reachable = new Set([0])

  if (validateStep0(state, scheme, options).valid) {
    reachable.add(1)
  }
  if (reachable.has(1) && validateStep1(state, scheme, options).valid) {
    if (state.isEarlyRedemption) {
      reachable.add(3)
    } else {
      reachable.add(2)
    }
  }
  if (reachable.has(2) && validateStep2(state, scheme, options).valid) {
    reachable.add(3)
  }
  if (reachable.has(3) && validateStep3(state, scheme, options).valid) {
    reachable.add(4)
  }

  return reachable
}
